//@ts-ignore
import React, { useRef } from 'react';
import { graphql } from 'gatsby';
import * as styles from './product-display-cards-gallery.module.css';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import './swipe-styles.css';
import {
    KontentRichText,
    KontentStringValue,
    KontentRadioMultiChoice,
    BrandColor,
    CTALocation,
    KontentMedia,
    KontentNumberValue,
} from '../../../types/KontentProps';
import {
    getColor,
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import RichText from '../Global/RichText';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import { ProductCardsStackView } from './ProductCardsStackView';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/virtual';
import { Navigation, Pagination, Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

interface Product {
    product_title?: KontentStringValue;
    product_description?: KontentRichText;
    product_image?: KontentMedia;
    badge?: KontentStringValue;
    badge_text_color?: BrandColor;
    badge_color?: BrandColor;
    price_field?: KontentStringValue;
    price_field_text_color?: BrandColor;
    price_field_color?: BrandColor;
    cta_configuration__cta_label?: KontentStringValue;
    cta_configuration__cta_location_anchor_point?: KontentStringValue;
    cta_configuration__cta_location?: CTALocation;
    cta_configuration__cta_window_action?: KontentRadioMultiChoice<
        'new_window' | 'same_window'
    >;
}

interface ProductDetail {
    elements: Product;
}

interface Products {
    value: ProductDetail[];
}

interface Props {
    headline?: KontentStringValue;
    number_of_products_to_display_on_mobile?: KontentNumberValue;
    reveal_more_copy?: KontentStringValue;
    products: Products;
    mobile_view?: KontentRadioMultiChoice<'stacked' | 'carousel'>;
    kontentItemId?: string;
}

const ProductDisplayCardsGallery: React.FC<Props> = ({
    headline,
    number_of_products_to_display_on_mobile,
    reveal_more_copy,
    products,
    mobile_view,
    kontentItemId,
}) => {
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const breakpoints = useBreakpoint();

    const modifiedProducts = {
        value: products.value.map((cardDetail: ProductDetail) => {
            const card = cardDetail.elements;
            return {
                productTitle: card?.product_title?.value,
                productDesc: card?.product_description,
                productImg: getMediaData(card?.product_image),
                badgeText: card?.badge?.value,
                badgeTextColor: getColor(card?.badge_text_color),
                badgeBackgroundColor: getColor(card?.badge_color),
                priceText: card?.price_field?.value,
                priceTextColor: getColor(card?.price_field_text_color),
                priceBackgroundColor: getColor(card?.price_field_color),
                ctaLabel: card?.cta_configuration__cta_label?.value,
                ctaAnchor:
                    card?.cta_configuration__cta_location_anchor_point?.value,
                ctaLocation: card?.cta_configuration__cta_location,
                ctaWindowAct:
                    getMultiChoice(
                        card?.cta_configuration__cta_window_action
                    ) === 'new_window'
                        ? '_blank'
                        : '_self',
            };
        }),
    };
    const uniqueId = `swiper-${Math.random().toString(36)?.substr(2, 9)}`;
    const headlineText = headline?.value;
    const noOfCardsToDisplayOnMobile =
        number_of_products_to_display_on_mobile?.value;
    const revealMoreText = reveal_more_copy?.value;
    const isMobileViewTypeStack = mobile_view?.value[0]?.codename == 'stacked';

    const ProductCardsCarouselView = () => {
        return (
            <div className={styles.containerWrapper}>
                <div className="containerA">
                    <div
                        className="swiper-button-prev cutomPrev"
                        id={`swiper-prev-${uniqueId}`}
                        ref={prevRef}
                    ></div>
                    {headlineText && (
                        <div className={styles.headline}>
                            <h3>{headlineText}</h3>
                        </div>
                    )}

                    <Swiper
                        className="customSwiper"
                        modules={[Virtual, Navigation, Pagination]}
                        centerInsufficientSlides
                        virtual
                        spaceBetween={10}
                        navigation={{
                            nextEl: `#swiper-next-${uniqueId}`,
                            prevEl: `#swiper-prev-${uniqueId}`,
                        }}
                        pagination={{
                            type: 'bullets',
                            enabled: true,
                            clickable: true,
                        }}
                        scrollbar={{ draggable: true }}
                        breakpoints={{
                            390: {
                                slidesPerView: 1,
                                spaceBetween: 10,
                            },
                            500: {
                                slidesPerView: 2,
                                spaceBetween: 10,
                            },
                            650: {
                                slidesPerView: 3,
                                spaceBetween: 10,
                            },
                            850: {
                                slidesPerView: 4,
                                spaceBetween: 15,
                            },
                            1100: {
                                slidesPerView: 5,
                                spaceBetween: 15,
                            },
                            1500: {
                                slidesPerView: 6,
                                spaceBetween: 20,
                            },
                        }}
                    >
                        {modifiedProducts.value.length &&
                            modifiedProducts.value.map((card, idx) => {
                                return (
                                    <SwiperSlide
                                        virtualIndex={idx}
                                        key={idx}
                                        className="customSlide"
                                    >
                                        <div className={styles.card}>
                                            <div className={styles.cardInner}>
                                                {card.badgeText && (
                                                    <div
                                                        className={styles.badge}
                                                    >
                                                        <p
                                                            style={{
                                                                background:
                                                                    card?.badgeBackgroundColor
                                                                        ? card?.badgeBackgroundColor
                                                                        : '#f06623',
                                                                color: card?.badgeTextColor
                                                                    ? card?.badgeTextColor
                                                                    : 'white',
                                                            }}
                                                        >
                                                            {card?.badgeText}
                                                        </p>
                                                    </div>
                                                )}
                                                {card.productImg?.url && (
                                                    <LazyImage
                                                        src={optimizeKontentImageUrl(
                                                            card.productImg.url,
                                                            {
                                                                ...imagePreset
                                                                    .card
                                                                    .desktop,
                                                                q: 95,
                                                            }
                                                        )}
                                                        srcSet={buildKontentSrcSet(
                                                            card.productImg.url,
                                                            imagePreset.card
                                                                .desktop.w,
                                                            {
                                                                fm: 'webp' as const,
                                                                q: 95,
                                                            }
                                                        )}
                                                        sizes="(max-width: 576px) 100vw, 33vw"
                                                        alt={
                                                            card.productImg
                                                                ?.name || ''
                                                        }
                                                        {...getDisplayDimensions(
                                                            card.productImg
                                                                .width,
                                                            card.productImg
                                                                .height,
                                                            imagePreset.card
                                                                .desktop.w
                                                        )}
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                    />
                                                )}
                                                {card.productTitle && (
                                                    <h4
                                                        className={
                                                            card.productDesc
                                                                ?.value ===
                                                            '<p><br></p>'
                                                                ? styles.growTitle
                                                                : ''
                                                        }
                                                    >
                                                        {card.productTitle}
                                                    </h4>
                                                )}
                                                {card.productDesc?.value !==
                                                    '<p><br></p>' && (
                                                    <div
                                                        className={
                                                            styles.description
                                                        }
                                                    >
                                                        {RichText(
                                                            card.productDesc,
                                                            false,
                                                            undefined,
                                                            undefined,
                                                            'product_description'
                                                        )}
                                                    </div>
                                                )}
                                                {card.priceText && (
                                                    <p
                                                        style={{
                                                            background:
                                                                card?.priceBackgroundColor
                                                                    ? card?.priceBackgroundColor
                                                                    : 'transparent',
                                                            color: card?.priceTextColor
                                                                ? card?.priceTextColor
                                                                : 'black',
                                                            fontWeight: 'bold',
                                                            padding: '4px 0',
                                                            margin: '0.5rem 0 1rem 0',
                                                        }}
                                                    >
                                                        {card.priceText}
                                                    </p>
                                                )}
                                                {card.ctaLabel && (
                                                    <div className={styles.cta}>
                                                        <CtaLink
                                                            href={
                                                                card?.ctaAnchor
                                                                    ? BuildLinkHref(
                                                                          card?.ctaLocation
                                                                      ) +
                                                                      '#' +
                                                                      card?.ctaAnchor
                                                                    : BuildLinkHref(
                                                                          card?.ctaLocation
                                                                      )
                                                            }
                                                            target={
                                                                card?.ctaWindowAct
                                                            }
                                                        >
                                                            {card.ctaLabel}
                                                        </CtaLink>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                );
                            })}
                    </Swiper>
                    <div
                        className="swiper-button-next cutomNext"
                        id={`swiper-next-${uniqueId}`}
                        ref={nextRef}
                    ></div>
                </div>
            </div>
        );
    };

    return (
        <div
            data-kontent-item-id={kontentItemId}
            style={{ display: 'contents' }}
        >
            {!breakpoints.sm && isMobileViewTypeStack ? (
                <ProductCardsStackView
                    noOfCardsToDisplayOnMobile={noOfCardsToDisplayOnMobile}
                    headlineText={headlineText}
                    modifiedProducts={modifiedProducts}
                    revealMoreText={revealMoreText}
                />
            ) : (
                <ProductCardsCarouselView />
            )}
        </div>
    );
};

export default ProductDisplayCardsGallery;

export const fragmentProductDisplayCardsGallery = graphql`
    fragment ProductDisplayCardsGallery on kontent_item_component___product_display {
        id
        system {
            id
            codename
        }
        elements {
            headline {
                value
            }
            reveal_more_copy {
                value
            }
            number_of_products_to_display_on_mobile {
                value
            }
            mobile_view {
                value {
                    codename
                }
            }
            products {
                value {
                    ... on kontent_item_content_item___product_display_item {
                        id
                        elements {
                            product_title {
                                value
                            }
                            product_image {
                                ...kontentImage
                            }
                            price_field {
                                value
                            }
                            badge {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_label {
                                value
                            }
                            price_field_color {
                                ...kontentColor
                            }
                            price_field_text_color {
                                ...kontentColor
                            }
                            product_description {
                                ...kontentRichText
                            }
                            badge_color {
                                ...kontentColor
                            }
                            badge_text_color {
                                ...kontentColor
                            }
                        }
                    }
                }
            }
        }
    }
`;
